import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { IEndUserCareer } from 'interfaces/CareerInterface'
import { noPreviewImage } from 'utills/image'
import { getCareerStatusText, getStatusPillClass } from 'utills/career-status'

type SortableDiscardRowProps = {
  item: IEndUserCareer
  reorderMode: boolean
  onClick: () => void
}

const SortableDiscardRow: React.FC<SortableDiscardRowProps> = ({
  item,
  reorderMode,
  onClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const statusText = getCareerStatusText(item)

  const style: React.CSSProperties = {
    // Suppress sortable transform while this row is the active drag — the
    // DragOverlay renders the floating ghost; keeping the source pinned avoids
    // the visual "disappear" on cross-section drag.
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    boxShadow:
      '0 1px 2px 0 rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.1)',
  }

  const dragProps = reorderMode ? { ...attributes, ...listeners } : {}

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      className={`flex items-center gap-2 border border-gray-light rounded-md px-3 py-2 bg-white ${
        reorderMode ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      onClick={reorderMode ? undefined : onClick}
      role={reorderMode ? undefined : 'button'}
      tabIndex={reorderMode ? undefined : 0}
      onKeyDown={
        reorderMode
          ? undefined
          : e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
      }
    >
      {reorderMode && (
        <span
          className="text-gray-medium shrink-0 pointer-events-none"
          aria-hidden="true"
        >
          <GripVertical size={16} />
        </span>
      )}
      <div className="flex items-center justify-between gap-3 flex-1 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={item.icon_url || noPreviewImage}
            alt={item.name}
            className="w-8 h-8 rounded-full object-cover shrink-0"
          />
          <span className="font-sans text-sm font-semibold text-brand truncate">
            {item.name}
          </span>
        </div>
        {!reorderMode && (
          <span
            className={`font-sans text-[11px] font-semibold px-2 py-0.5 rounded-md shrink-0 ${getStatusPillClass(
              statusText,
            )}`}
          >
            {statusText}
          </span>
        )}
      </div>
    </div>
  )
}

export default SortableDiscardRow
